import { apiFetch } from "./client";

export interface ProviderStatus {
  provider: string;
  method: "tenant_oauth" | "tenant_api_key" | "platform_vault" | "none";
  ready: boolean;
  label: string;
  expiresAt?: number;
}

export type WebSearchProvider = "duckduckgo" | "brave" | "perplexity";

export interface TenantModelConfig {
  id?: string;
  companyId: string;
  defaultModel?: string | null;
  /** Catalogue model_id of the tenant's default image model, e.g. "azure/gpt-image-2". */
  defaultImageModel?: string | null;
  // OpenAI
  openaiApiKey?: string | null;
  hasOpenaiApiKey: boolean;
  openaiCodexConnected: boolean;
  openaiCodexAccountId?: string | null;
  openaiCodexExpires?: number | null;
  // Anthropic
  anthropicConnected: boolean;
  anthropicExpires?: number | null;
  anthropicBackupConnected: boolean;
  anthropicBackupExpires?: number | null;
  anthropicActiveSlot: "primary" | "backup";
  anthropicApiKey?: string | null;
  hasAnthropicApiKey: boolean;
  // Google
  googleApiKey?: string | null;
  hasGoogleApiKey: boolean;
  // Groq
  groqApiKey?: string | null;
  hasGroqApiKey: boolean;
  // Azure Foundry
  azureFoundryApiKey?: string | null;
  hasAzureFoundryApiKey: boolean;
  azureFoundryEndpoint?: string | null;
  // Web search
  webSearchProvider?: string | null;
  /** What agents actually use once synced, after key availability is applied. */
  effectiveWebSearchProvider: WebSearchProvider;
  /** Only providers with a key on file. DuckDuckGo is always included. */
  availableWebSearchProviders: WebSearchProvider[];
  hasBraveApiKey: boolean;
  perplexityApiKey?: string | null;
  hasPerplexityApiKey: boolean;
  // Unified status
  providers: Record<string, ProviderStatus>;
}

export const tenantModelConfigApi = {
  get: (tenantId: string) =>
    apiFetch<TenantModelConfig>(`/admin/tenants/${tenantId}/model-config`),

  update: (tenantId: string, data: Record<string, string | undefined>) =>
    apiFetch<TenantModelConfig>(`/admin/tenants/${tenantId}/model-config`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  removeProviderKey: (tenantId: string, provider: string) =>
    apiFetch<{ success: boolean }>(
      `/admin/tenants/${tenantId}/model-config/provider-key/${provider}`,
      { method: "DELETE" },
    ),

  // OpenAI OAuth
  oauthStart: (tenantId: string) =>
    apiFetch<{ authUrl: string; state: string }>(
      `/admin/tenants/${tenantId}/model-config/oauth/start`,
      { method: "POST" },
    ),

  oauthComplete: (tenantId: string, callbackUrl: string) =>
    apiFetch<{ accountId: string; expires: number }>(
      `/admin/tenants/${tenantId}/model-config/oauth/complete`,
      { method: "POST", body: JSON.stringify({ callbackUrl }) },
    ),

  oauthRefresh: (tenantId: string) =>
    apiFetch<{ expires: number }>(
      `/admin/tenants/${tenantId}/model-config/oauth/refresh`,
      { method: "POST" },
    ),

  oauthDisconnect: (tenantId: string) =>
    apiFetch(`/admin/tenants/${tenantId}/model-config/oauth`, { method: "DELETE" }),

  // Anthropic OAuth — slot-parameterized (primary | backup)
  oauthAnthropicStart: (tenantId: string, slot: "primary" | "backup" = "primary") =>
    apiFetch<{ authUrl: string; state: string; slot: "primary" | "backup" }>(
      `/admin/tenants/${tenantId}/model-config/oauth/anthropic/start?slot=${slot}`,
      { method: "POST" },
    ),

  oauthAnthropicComplete: (
    tenantId: string,
    callbackUrl: string,
    slot: "primary" | "backup" = "primary",
  ) =>
    apiFetch<{ expires: number; slot: "primary" | "backup" }>(
      `/admin/tenants/${tenantId}/model-config/oauth/anthropic/complete?slot=${slot}`,
      { method: "POST", body: JSON.stringify({ callbackUrl }) },
    ),

  oauthAnthropicRefresh: (tenantId: string, slot: "primary" | "backup" = "primary") =>
    apiFetch<{ expires: number; slot: "primary" | "backup" }>(
      `/admin/tenants/${tenantId}/model-config/oauth/anthropic/refresh?slot=${slot}`,
      { method: "POST" },
    ),

  oauthAnthropicDisconnect: (tenantId: string, slot: "primary" | "backup" = "primary") =>
    apiFetch(
      `/admin/tenants/${tenantId}/model-config/oauth/anthropic?slot=${slot}`,
      { method: "DELETE" },
    ),

  oauthAnthropicPromote: (tenantId: string, slot: "primary" | "backup") =>
    apiFetch<{ success: boolean; activeSlot: "primary" | "backup" }>(
      `/admin/tenants/${tenantId}/model-config/oauth/anthropic/promote`,
      { method: "POST", body: JSON.stringify({ slot }) },
    ),

  // Sync to agents
  sync: (tenantId: string) =>
    apiFetch<{
      synced: number;
      results: Array<{ agentId: string; agentName: string; success: boolean; error?: string }>;
    }>(`/admin/tenants/${tenantId}/model-config/sync`, { method: "POST" }),
};
